import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLang } from '../context/LangContext';
import { ChannelBadges } from '../components/ChannelBadges';
import { 
  ArrowLeft, 
  Eye, 
  Calendar, 
  User, 
  MapPin,
  Play,
  Share2,
  ArrowRight,
  Loader2,
  Sprout
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const themeConfig = {
  land: { icon: '🌍', label: 'Land', color: 'bg-amber-100 text-amber-800' },
  finance: { icon: '💰', label: 'Finance', color: 'bg-green-100 text-green-800' },
  inputs: { icon: '🌱', label: 'Inputs', color: 'bg-lime-100 text-lime-800' },
  markets: { icon: '🏪', label: 'Markets', color: 'bg-blue-100 text-blue-800' },
  climate: { icon: '☀️', label: 'Climate', color: 'bg-orange-100 text-orange-800' },
  skills: { icon: '📚', label: 'Skills', color: 'bg-purple-100 text-purple-800' },
};

const countryFlags = {
  tanzania: '🇹🇿', ghana: '🇬🇭', malawi: '🇲🇼', rwanda: '🇷🇼',
  mali: '🇲🇱', 'burkina-faso': '🇧🇫', mozambique: '🇲🇿',
};

export const StoryDetailPage = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    fetchStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, lang]);

  const fetchStory = async () => {
    setLoading(true);
    try {
      // 1) Fetch English (fast — title/caption already cached, full_content native).
      //    Render immediately.
      const baseRes = await fetch(`${API_URL}/api/stories/public/${storyId}?lang=en`);
      if (baseRes.ok) {
        const baseData = await baseRes.json();
        setStory(baseData);
        setLoading(false);
        // 2) If user requested a non-English language, fetch the translated version
        //    in the background and swap fields in when it arrives.
        if (lang && lang !== 'en') {
          fetch(`${API_URL}/api/stories/public/${storyId}?lang=${lang}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((trData) => {
              if (trData) setStory(trData);
            })
            .catch(() => {});
        }
      }
    } catch (error) {
      console.error('Failed to fetch story:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center page-transition">
        <div className="text-center animate-fade-in-up">
          <Sprout className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Story not found</h2>
          <p className="text-gray-600 mb-6">This story may have been removed or is still under review.</p>
          <Button onClick={() => navigate('/stories')} className="btn-premium bg-[#07743F] hover:bg-[#065a32] text-white">
            Browse Stories
          </Button>
        </div>
      </div>
    );
  }

  const theme = themeConfig[story.theme] || { icon: '📖', label: story.theme, color: 'bg-gray-100 text-gray-800' };
  const flag = countryFlags[story.country_id] || '🌍';
  const contentParagraphs = story.full_content?.split('\n\n') || [story.caption];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white page-transition">
      {/* Breadcrumb Navigation */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/stories')}
            className="text-[#07743F] hover:bg-green-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Stories
          </Button>
        </div>
      </div>

      {/* Content */}
      <article className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Theme & Status Badge */}
          <div className="flex justify-center gap-3 mb-6 animate-bounce-in">
            <Badge className={`${theme.color} px-4 py-2 text-sm`}>
              <span className="mr-2">{theme.icon}</span>
              {theme.label}
            </Badge>
            <Badge className="bg-[#72BF44]/10 text-[#07743F] px-4 py-2 text-sm">
              {story.status === 'published' ? 'Published' : 'Approved'}
            </Badge>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 text-center leading-tight animate-hero-text">
            {story.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 mb-8 animate-hero-text-delay">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{flag}</span>
              <span className="font-semibold">{story.country_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#07743F]" />
              <span>{story.author_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#07743F]" />
              <span>{story.views || 0} views</span>
            </div>
          </div>

          {Array.isArray(story.channels_shared) && story.channels_shared.length > 0 && (
            <div className="mb-8 animate-fade-in-up">
              <ChannelBadges channels={story.channels_shared} label="Shared on" />
            </div>
          )}

          {/* Image/Video */}
          <div className="mb-12 animate-scale-in">
            {story.video_url && showVideo ? (
              <Card className="overflow-hidden bg-gray-100 border-2 border-[#72BF44]">
                <CardContent className="p-0">
                  <div className="aspect-video flex items-center justify-center p-8">
                    <div className="text-center">
                      <Play className="w-16 h-16 text-[#07743F] mx-auto mb-4" />
                      <p className="text-gray-700 mb-2">Video content would load here</p>
                      <p className="text-sm text-gray-500 mb-4">URL: {story.video_url}</p>
                      <Button 
                        variant="outline"
                        onClick={() => setShowVideo(false)}
                        className="border-[#07743F] text-[#07743F]"
                      >
                        View Image
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="relative group">
                <div className="aspect-video overflow-hidden rounded-2xl shadow-lg bg-gray-100">
                  <img
                    src={story.image_url || 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800'}
                    alt={story.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      const fb = 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800';
                      if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                    }}
                  />
                </div>
                {story.video_url && (
                  <Button
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 shadow-xl"
                    onClick={() => setShowVideo(true)}
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Watch Video
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Caption Highlight */}
          <Card className="mb-12 bg-gradient-to-r from-[#07743F]/5 to-[#72BF44]/5 border-2 border-[#72BF44] animate-fade-in-up">
            <CardContent className="p-8">
              <p className="text-xl md:text-2xl text-gray-800 leading-relaxed font-medium text-center italic">
                "{story.caption}"
              </p>
            </CardContent>
          </Card>

          {/* Full Content */}
          {story.full_content && (
            <div className="prose prose-lg max-w-none mb-12 animate-fade-in-up">
              {contentParagraphs.map((paragraph, idx) => (
                <p key={idx} className="text-gray-700 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          )}

          {/* Author Card */}
          <Card className="mb-12 border-2 border-gray-200 animate-fade-in-up">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#07743F] to-[#72BF44] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {story.author_name?.charAt(0) || 'Y'}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{story.author_name}</h3>
                  <p className="text-gray-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {story.country_name}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Share */}
          <div className="flex justify-center mb-12 animate-bounce-in">
            <Button
              size="lg"
              className="bg-[#07743F] hover:bg-[#065a32] text-white shadow-lg"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share This Story
            </Button>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-[#07743F] to-[#72BF44] rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">Have Your Own Story?</h3>
            <p className="text-green-100 mb-6">
              Your voice matters. Share your journey and inspire young farmers across Africa.
            </p>
            <Button 
              onClick={() => navigate('/submit')}
              className="bg-white text-[#07743F] hover:bg-green-50"
            >
              Tell Your Story
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default StoryDetailPage;
